import { useState, useEffect, createContext, useContext } from 'react';
import apiService from '../services/api.js';
import supabase from '../integrations/supabase.js';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasCourseSelection, setHasCourseSelection] = useState(null);
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  const [lastAuthCheck, setLastAuthCheck] = useState(0);

  const checkUserCourseSelection = async (userId) => {
    // Prevent duplicate calls
    if (hasCourseSelection !== null) {

      return hasCourseSelection;
    }
    
    try {
  
      const response = await apiService.checkUserCourseSelection();
      
      if (response && response.has_selection) {

        setHasCourseSelection(true);
        return true;
      } else {

        setHasCourseSelection(false);
        return false;
      }
    } catch (error) {
      
      setHasCourseSelection(false);
      return false;
    }
  };

  const forceRefreshCourseSelection = async () => {
    // Add a small delay to prevent rapid calls
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
  
      const response = await apiService.checkUserCourseSelection();
      
      if (response && response.has_selection) {

        setHasCourseSelection(true);
        return true;
      } else {

        setHasCourseSelection(false);
        return false;
      }
    } catch (error) {
      
      setHasCourseSelection(false);
      return false;
    }
  };

  const checkAuth = async () => {
    // Prevent rapid auth checks (debounce)
    const now = Date.now();
    if (now - lastAuthCheck < 2000) { // 2 second debounce
      
      return;
    }
    setLastAuthCheck(now);
    
    try {

      
      // Get current session from Supabase
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session error:', sessionError);
        setUser(null);
        setLoading(false);
        return;
      }
      
      if (!session) {

        setUser(null);
        setLoading(false);
        return;
      }
      
      
      
      // Create user object from session
      const userObject = {
        id: session.user.id,
        email: session.user.email,
        full_name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
        first_name: session.user.user_metadata?.first_name,
        last_name: session.user.user_metadata?.last_name,
        avatar_url: session.user.user_metadata?.avatar_url,
        provider: session.user.app_metadata?.provider,
        user_metadata: session.user.user_metadata,
        app_metadata: session.user.app_metadata
      };
      
      setUser(userObject);
      
      // Store tokens for backend API
      localStorage.setItem('user_token', session.access_token);
      localStorage.setItem('refresh_token', session.refresh_token);
      
      // Set loading to false immediately for better UX
      setLoading(false);
      
      // Course selection check only if not already checked and not on login page
      if (hasCourseSelection === null && window.location.pathname !== '/login') {

        const courseSelectionPromise = checkUserCourseSelection(userObject.id);
        const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
        
        const hasSelection = await Promise.race([courseSelectionPromise, timeoutPromise]);
      }
      
      // Backend sync in background (non-blocking)
      setTimeout(async () => {
        try {
          const backendUser = await apiService.getCurrentUser();
          if (backendUser) {
            setUser(prevUser => ({
              ...prevUser,
              ...backendUser
            }));
          }
        } catch (backendError) {
  
        }
      }, 100);
      
    } catch (error) {
      console.error('❌ Auth check error:', error);
      setUser(null);
      setLoading(false);
    }
  };

  // Check if user is logged in on app start
  useEffect(() => {
    // Only check auth if not on login page
    if (window.location.pathname !== '/login') {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await apiService.login(email, password);
      
      if (response.status === 'success') {
        // Store tokens
        localStorage.setItem('user_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        
        // Set user
        setUser(response.user);
        return { success: true };
      } else {
        setError(response.error || 'Login failed');
        return { success: false, error: response.error };
      }
    } catch (error) {
      setError('Login failed. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, name = '') => {
    try {
      setError(null);
      const response = await apiService.signup(email, password, name);
      
      if (response.status === 'success') {
        // Store tokens
        localStorage.setItem('user_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        
        // Set user
        setUser(response.user);
        return { success: true };
      } else {
        setError(response.error || 'Signup failed');
        return { success: false, error: response.error };
      }
    } catch (error) {
      setError('Signup failed. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleOAuthLogin = async (userData) => {
    try {

      
      // Check if user is already registered to prevent duplicate calls
      const existingUser = user;
      if (existingUser && existingUser.email === userData.email) {

        return { success: true };
      }

      const userObject = {
        id: userData.id,
        email: userData.email,
        full_name: userData.user_metadata?.full_name || userData.user_metadata?.name,
        first_name: userData.user_metadata?.first_name,
        last_name: userData.user_metadata?.last_name,
        avatar_url: userData.user_metadata?.avatar_url,
        provider: userData.app_metadata?.provider,
        user_metadata: userData.user_metadata,
        app_metadata: userData.app_metadata
      };
      
      // Set user immediately
      setUser(userObject);
      
      // Only register with backend if explicitly requested
      // This prevents automatic registration during auth callback
      return { success: true };
    } catch (error) {
      console.error('❌ OAuth login handler error:', error);
      setError('OAuth login failed. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const registerOAuthUser = async (userData) => {
    // Prevent duplicate registration attempts
    if (registrationAttempted) {
      
      return { success: true };
    }
    
    try {
      
      setRegistrationAttempted(true);
      
      const registerResponse = await apiService.registerOAuthUser({
        email: userData.email,
        name: userData.full_name || userData.first_name || userData.email.split("@")[0],
        avatar_url: userData.avatar_url
      });
      
      if (registerResponse.status === 'success') {

        // Update user with backend data if available
        if (registerResponse.user) {
          setUser(registerResponse.user);
        }
        return { success: true };
      } else {
        throw new Error('Registration failed');
      }
    } catch (registerError) {
      // Check if this is a duplicate key error (user already exists)
      if (registerError.message && (
        registerError.message.includes('duplicate key value violates unique constraint') ||
        registerError.message.includes('already exists') ||
        registerError.message.includes('23505')  // PostgreSQL duplicate key error code
      )) {

        return { success: true };
      } else {

        // Reset the flag so we can try again later
        setRegistrationAttempted(false);
        throw registerError;
      }
    }
  };

  const logout = async () => {
    try {
      // Clear Supabase session if available
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase logout error:', error);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and user
      localStorage.removeItem('user_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setError(null);
      setHasCourseSelection(null);
    }
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.refreshToken(refreshToken);
      
      if (response.status === 'success') {
        localStorage.setItem('user_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        return true;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  const value = {
    user,
    loading,
    error,
    hasCourseSelection,
    setHasCourseSelection,
    login,
    signup,
    logout,
    refreshToken,
    handleOAuthLogin,
    registerOAuthUser,
    checkUserCourseSelection,
    forceRefreshCourseSelection
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 